import { z } from 'zod';

export const QUESTION_SCOPES = ['attendee', 'addon'] as const;

export type QuestionScope = (typeof QUESTION_SCOPES)[number];

export const ADDON_ANSWERS_FIELD_KEY = 'addonAnswers';

export const ADDON_QUESTION_EXCLUDED_TYPES = ['file', 'signature'] as const;

export type AddonAnswerValue = string | number | string[] | null;

export type AddonAnswers = Record<string, Array<Record<string, AddonAnswerValue>>>;

const addonAnswerValueSchema = z.union([z.string(), z.number(), z.array(z.string()), z.null()]);

export type UnitAnswers = Record<string, AddonAnswerValue>;

// A follow-up hidden in the form is set to undefined, and superjson keeps the
// key, so undefined is accepted here and dropped rather than failing the save.
export const unitAnswersSchema = z
	.record(z.string(), addonAnswerValueSchema.optional())
	.transform(
		(unit): UnitAnswers =>
			Object.fromEntries(
				Object.entries(unit).filter((entry): entry is [string, AddonAnswerValue] => entry[1] !== undefined)
			)
	);

export const addonAnswersSchema = z.record(z.string(), z.array(unitAnswersSchema));

export type UnitAnswersInput = z.input<typeof unitAnswersSchema>;

export type AddonAnswersInput = z.input<typeof addonAnswersSchema>;

export function readAddonAnswers(value: unknown): AddonAnswers {
	let candidate = value;
	if (typeof candidate === 'string') {
		try {
			candidate = JSON.parse(candidate);
		} catch {
			return {};
		}
	}
	const parsed = addonAnswersSchema.safeParse(candidate);
	return parsed.success ? parsed.data : {};
}

export function readUnitAnswers(value: unknown): UnitAnswers {
	const parsed = unitAnswersSchema.safeParse(value);
	return parsed.success ? parsed.data : {};
}

export function countUnits(unitIds: string[]): Record<string, number> {
	const counts: Record<string, number> = {};
	for (const id of unitIds) counts[id] = (counts[id] ?? 0) + 1;
	return counts;
}

export function trimAddonAnswers(answers: AddonAnswers, units: Record<string, number>): AddonAnswers {
	const trimmed: AddonAnswers = {};
	for (const [addonId, perUnit] of Object.entries(answers)) {
		const count = units[addonId] ?? 0;
		if (count <= 0) continue;
		trimmed[addonId] = perUnit.slice(0, count);
	}
	return trimmed;
}

export function hasAddonAnswer(value: unknown): boolean {
	if (value === null || value === undefined || value === '') return false;
	if (typeof value === 'number') return !Number.isNaN(value);
	if (typeof value === 'string') return value.trim().length > 0;
	if (Array.isArray(value)) return value.length > 0;
	return true;
}
